import { ClassType } from 'globalTypes';

import Group from 'entities/Group';

import { nonV2ClassDefaults } from '../fragments/__generated__/nonV2ClassDefaults';

/* GroupDataOps
  This helper file is designed to bridge the gap between
     backbone entities of group and purely using GQL.

  It is designed to use either the Group entity object,
  or a specific shape of the data to get the results.

  The shaping of the data is used instead of GQL types because 
  we would end up supporting every single GQL type instead 
    of just leveraging 'strong' duck typing

******/

/* ****************************************************************

PLEASE MAKE SURE TO KEEP GroupDataOps.flow.js in sync!

**************************************************************** */

function isGroupEntity(group: any): group is Group {
  return group instanceof Group;
}

type ClassNameGroup = { className: string } | { class_name: string } | GroupOrNonV2;

// Using both Group entity type and NonClassV2 from GQL
// this is to ensure any changes on the class are accounted for here
// and that each function -typewise- should behave well with a NonV2Class or Group
// Please Note:
//    The additional type shapes for each method are to allow antyhing that
//      resembles a class object to be used.  This strategy should be employed in
//      any future additions.
type GroupOrNonV2 = Group | nonV2ClassDefaults;

export default {
  // prefer this is used internally to this file only, added in case you have a specific case that doesn't fit belonging in this file.
  // otherwise methods should be incorporated into this module
  isGroupEntity,

  hasIdOrUuid(group?: GroupOrNonV2 | { id?: string } | { uuid?: string } | null): boolean {
    if (typeof group === 'undefined' || group === null) {
      return false;
    }
    if ('id' in group) {
      return !!group.id;
    }
    if ('uuid' in group) {
      return !!group.uuid;
    }
    return false;
  },

  canJoin(
    group:
      | GroupOrNonV2
      | { permissions: { can_join: boolean | null } | null }
      | { canJoin: boolean }
  ): boolean {
    if (isGroupEntity(group)) {
      return group.canJoin();
    }

    if ('canJoin' in group) {
      return group.canJoin;
    }

    return !!(group.permissions && group.permissions.can_join);
  },

  getClassName(group: ClassNameGroup): string {
    if (isGroupEntity(group)) {
      return group.getName();
    }
    if ('className' in group) {
      return group.className;
    } else {
      return group.class_name;
    }
  },
  isStaffClass(group: GroupOrNonV2 | { type: string }): boolean {
    if (isGroupEntity(group)) {
      return group.isStaffClass();
    } else {
      return group.type === ClassType.organization || group.type === ClassType.district;
    }
  },
  getOrganizationName(
    group: GroupOrNonV2 | { organization: { name: string } | null }
  ): string | null {
    if (isGroupEntity(group)) {
      const org = group.getOrganization();
      return org ? org.getName() : null;
    } else {
      return group.organization && group.organization.name;
    }
  },
  getPossessiveSignature(
    group: GroupOrNonV2 | ({ possessiveSignature: string } & ClassNameGroup)
  ): string {
    if (isGroupEntity(group)) {
      return group.getPossessiveSignature();
    } else {
      return `${group.possessiveSignature} ${this.getClassName(group)}`;
    }
  },
  isSMSEnabled(group: GroupOrNonV2 | { subscribe: { sms: {} | null } }) {
    if (isGroupEntity(group)) {
      return group.isSMSEnabled();
    }
    return !!group.subscribe.sms;
  },

  getCode(group: GroupOrNonV2 | { classCode: string | null }): string | null {
    if (isGroupEntity(group)) {
      return group.getCode() || null;
    }
    return group.classCode;
  },

  getCodeOrError(group: GroupOrNonV2 | { classCode: string | null }): string {
    const code = this.getCode(group);
    if (code) {
      return code;
    } else {
      console.warn({ group });
      console.error('Group getCodeOrError() falling back to "error"');
      return 'error';
    }
  },

  parseAcceptableGroupCode(groupCode: string): string | null {
    // leaving this here for now in the interest of staying DRY and not having cyclical imports
    //  will move the code to this file when we remove Group Entity.
    return Group.parseAcceptableGroupCode(groupCode);
  },
};
